import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {ScrollArea} from 'src/design/atoms/ScrollArea';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {useDisclosure} from '@mantine/hooks';
import {IconPlus, IconTrash} from '@tabler/icons-react';
import clsx from 'clsx';
import type {FC} from 'react';
import {useState} from 'react';
import type {ConversationResponse} from 'src/api/ProactApiClient';
import {ActionIcon} from 'src/design/atoms/ActionIcon';
import {Button} from 'src/design/atoms/Button';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {formatUtcShortDate} from 'src/shared/utils/formatters';
import classes from './Assistant.module.css';

export interface ConversationListProps {
  conversations: ConversationResponse[] | undefined;
  activeId: number | undefined;
  isLoading: boolean;
  isError: boolean;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onNewConversation: () => void;
  isCreating: boolean;
  /** sm+ fills the fixed-height column and scrolls internally; mobile flows. */
  fillHeight?: boolean;
}

export const ConversationList: FC<ConversationListProps> = ({
  conversations,
  activeId,
  isLoading,
  isError,
  onSelect,
  onDelete,
  onNewConversation,
  isCreating,
  fillHeight = true,
}) => {
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [confirmOpened, confirmHandlers] = useDisclosure(false);

  const requestDelete = (id: number) => {
    setPendingDeleteId(id);
    confirmHandlers.open();
  };

  const confirmDelete = () => {
    if (pendingDeleteId !== null) onDelete(pendingDeleteId);
    setPendingDeleteId(null);
    confirmHandlers.close();
  };

  return (
    <SectionCard
      title="Conversations"
      h={fillHeight ? '100%' : undefined}
      rightSection={
        <Button
          size="compact-sm"
          leftSection={<IconPlus size={16} />}
          onClick={onNewConversation}
          loading={isCreating}>
          New
        </Button>
      }
      style={{display: 'flex', flexDirection: 'column'}}>
      <Box style={{flex: fillHeight ? 1 : undefined, minHeight: 0, display: 'flex'}}>
        {isLoading ? (
          <Center py="xl" style={{flex: 1}}>
            <Loader size="sm" />
          </Center>
        ) : isError ? (
          <Text size="sm" c="red" p="md">
            Unable to load conversations.
          </Text>
        ) : !conversations || conversations.length === 0 ? (
          <Text size="sm" c="dimmed" p="md">
            No conversations yet.
          </Text>
        ) : (
          <ScrollArea
            type="auto"
            style={{flex: 1}}
            styles={fillHeight ? undefined : {viewport: {overflow: 'visible'}}}>
            <Stack gap={2} p="xs">
              {conversations.map(conversation => {
                const active = conversation.id === activeId;
                return (
                  <Box
                    key={conversation.id}
                    className={clsx(classes.row, active && classes.rowActive)}>
                    <UnstyledButton
                      style={{flex: 1, minWidth: 0}}
                      onClick={() => onSelect(conversation.id)}>
                      <Text size="sm" fw={500} truncate>
                        {conversation.title || 'New conversation'}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {formatUtcShortDate(conversation.updatedAt)}
                      </Text>
                    </UnstyledButton>
                    <ActionIcon
                      className={classes.rowDelete}
                      tooltipText="Delete conversation"
                      color="red"
                      onClick={() => requestDelete(conversation.id)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Box>
                );
              })}
            </Stack>
          </ScrollArea>
        )}
      </Box>

      <ConfirmDialog
        opened={confirmOpened}
        onClose={() => confirmHandlers.close()}
        onConfirm={confirmDelete}
        confirmationButtonColor="red"
        title="Delete this conversation?"
        body="This permanently removes the conversation and its messages."
      />
    </SectionCard>
  );
};
